/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Loads user and buyer context when the event is available.
 * @module pages/viewAppointments/useViewAppointmentsUserContextEffect
 */

import { useEffect } from "react";
import { logger, sanitizeError } from "../../utils/logger.js";
import { loadViewAppointmentsUserContext } from "../../logic/appointments/viewAppointmentsInitData.js";

/**
 * @param {Object} ctx - ViewAppointments context
 */
const useViewAppointmentsUserContextEffect = (ctx) => {
  const {
    t,
    navigate,
    eventData,
    setIsLoading,
    setUserData,
    setParticipatingCompanyData,
    setParticipatingCompanies,
    setBuyerCompanies,
    setBuyerExecutives,
    setRequirements,
  } = ctx;

  useEffect(() => {
    const fetchAllData = async () => {
      const userId = localStorage.getItem("userId");

      if (!userId || !eventData?._id) {
        return;
      }

      setIsLoading(true);
      try {
        const context = await loadViewAppointmentsUserContext({
          userId,
          eventId: eventData._id,
          navigate,
          t,
        });

        if (!context) {
          return;
        }

        setUserData(context.user);
        setParticipatingCompanyData(context.participatingCompanyData);
        setParticipatingCompanies(context.participatingCompanies);
        setBuyerCompanies(context.buyerCompanies);
        setBuyerExecutives(context.buyerExecutives);
        setRequirements(context.requirements);
      } catch (error) {
        logger.error("Error fetching data:", { err: sanitizeError(error) });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, [
    t,
    eventData,
    navigate,
    setIsLoading,
    setUserData,
    setParticipatingCompanyData,
    setParticipatingCompanies,
    setBuyerCompanies,
    setBuyerExecutives,
    setRequirements,
  ]);
};

export default useViewAppointmentsUserContextEffect;
